import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Tv } from 'lucide-react';
import api from '../utils/api';
import ShareButton from '../components/ShareButton';
import { getImageUrl } from '../utils/imageUrl';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState(['Software', 'Digital Bundles', 'Templates', 'Tools', 'Other']);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setCategory(categoryParam);
    }
    fetchProducts();
    fetchCategories();
  }, [searchParams]);

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      const categoryParam = searchParams.get('category');
      if (categoryParam) params.append('category', categoryParam);
      if (search) params.append('search', search);
      
      const response = await api.get(`/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/products/categories/list');
      if (response.data && response.data.length > 0) {
        setCategories(response.data);
      }
    } catch (error) {
      console.log('Using default categories');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  const handleCategoryChange = (newCategory) => {
    if (newCategory) {
      setSearchParams({ category: newCategory });
    } else {
      setSearchParams({});
    }
    setCategory(newCategory);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="products-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-4" data-testid="products-title">
              Digital Products
            </h1>
            <p className="text-muted-foreground text-lg">
              Explore our premium collection of digital products
            </p>
          </div>
          <Link
            to="/display/products"
            className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 px-6 py-3 rounded-xl font-semibold hover:bg-green-500/30 transition-all w-fit"
            data-testid="display-tv-btn"
          >
            <Tv className="w-5 h-5" />
            Display TV
          </Link>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all"
                data-testid="search-input"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all"
              data-testid="search-button"
            >
              Search
            </button>
          </form>

          {/* Category Filters */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleCategoryChange('')}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                !category
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white/5 text-foreground hover:bg-white/10'
              }`}
              data-testid="category-all"
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-4 py-2 rounded-full font-medium transition-all ${
                  category === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white/5 text-foreground hover:bg-white/10'
                }`}
                data-testid={`category-${cat.toLowerCase().replace(' ', '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
            {products.map((product) => (
              <div
                key={product.id}
                className="product-card p-6 relative"
                data-testid={`product-card-${product.id}`}
              >
                {/* Share Button */}
                <div className="absolute top-4 right-4 z-10">
                  <ShareButton
                    type="product"
                    itemId={product.id}
                    itemName={product.name}
                  />
                </div>
                
                <Link to={`/products/${product.id}`}>
                  {product.images && product.images[0] && (
                    <div className="aspect-video rounded-lg overflow-hidden mb-4">
                      <img
                        src={getImageUrl(product.images[0])}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="mb-2">
                    <span className="text-xs font-semibold text-secondary px-3 py-1 bg-secondary/20 rounded-full">
                      {product.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex items-center gap-3">
                    {product.original_price && (
                      <span className="text-muted-foreground line-through text-sm">
                        ₹{product.original_price}
                      </span>
                    )}
                    <span className="text-2xl font-bold text-primary">
                      ₹{product.price}
                    </span>
                    {product.original_price && (
                      <span className="text-xs font-bold text-accent">
                        {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                      </span>
                    )}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16" data-testid="no-products">
            <p className="text-muted-foreground text-lg">No products found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
